import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';

export type RestaurantSpace = TDynamicData;

export type RestaurantSpaceCategory = {
  id: number;
  name: string;
  slug: string;
  spaces: RestaurantSpace[];
};

export const RESTAURANT_SPACES_SECTION_ID = 7;
export const RESTAURANT_SPACES_DESCRIPTION_FIELD_ID = 25;
export const RESTAURANT_SPACES_COVER_FIELD_ID = 26;
export const RESTAURANT_SPACES_ALBUM_FIELD_ID = 27;
export const RESTAURANT_SPACES_CATEGORY_FIELD_ID = 28;

export function slugifyRestaurantSpaceCategory(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function getRestaurantSpaceDescription(space: RestaurantSpace) {
  return (
    space.fields.find(field => field.field.id === RESTAURANT_SPACES_DESCRIPTION_FIELD_ID)?.value ??
    ''
  );
}

export function getRestaurantSpaceCover(space: RestaurantSpace) {
  return (
    space.fields.find(field => field.field.id === RESTAURANT_SPACES_COVER_FIELD_ID)?.image?.url ??
    space.fields.find(field => field.field.id === RESTAURANT_SPACES_ALBUM_FIELD_ID)?.images?.[0]
      ?.url ??
    space.image?.url ??
    '/images/placeholder.png'
  );
}

export function getRestaurantSpaceAlbum(space: RestaurantSpace) {
  const cover = getRestaurantSpaceCover(space);
  const album =
    space.fields.find(field => field.field.id === RESTAURANT_SPACES_ALBUM_FIELD_ID)?.images ?? [];

  const images = [cover, ...album.map(image => image.url).filter(Boolean)];

  return [...new Set(images)];
}

export function groupRestaurantSpacesByCategory(spaces: RestaurantSpace[]) {
  const categories = new Map<number, RestaurantSpaceCategory>();

  for (const space of spaces) {
    const categoryValues =
      space.fields.find(field => field.field.id === RESTAURANT_SPACES_CATEGORY_FIELD_ID)?.values ??
      [];

    for (const category of categoryValues) {
      if (!categories.has(category.id)) {
        categories.set(category.id, {
          id: category.id,
          name: category.name,
          slug: slugifyRestaurantSpaceCategory(category.name),
          spaces: [],
        });
      }

      categories.get(category.id)?.spaces.push(space);
    }
  }

  return [...categories.values()];
}
